import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart3, Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/**
 * Dashboard widget — Volume da equipa nos próximos 7 dias.
 *
 * Mostra ao Admin/Direcção/DGO/Gestor o volume de RTs agendadas
 * discriminado por dia + Consultor, para balancear a carga da equipa.
 *
 * Reutiliza o endpoint ``/api/rts-calendar?start_date=&end_date=`` que
 * já suporta ranges arbitrários.
 */
function pad(n) { return String(n).padStart(2, "0"); }
function ymd(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
const WEEKDAYS_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function TeamVolumeWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const start = new Date();
    const end = new Date();
    end.setDate(end.getDate() + 6);
    const params = new URLSearchParams({
      start_date: ymd(start),
      end_date: ymd(end),
    });
    api.get(`/rts-calendar?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i);
    return d;
  });

  // Aggregate per-day counts + per-consultor color counts.
  const dayStats = days.map((d) => {
    const iso = ymd(d);
    const evs = (data?.events || []).filter(
      (e) => (e.scheduled_qt_at || "").slice(0, 10) === iso
    );
    const byConsultor = {};
    for (const ev of evs) {
      const key = ev.consultor_id || "__none__";
      byConsultor[key] = {
        color: ev.consultor_color || "#94A3B8",
        name: ev.consultor_name || "Sem consultor",
        count: (byConsultor[key]?.count || 0) + 1,
      };
    }
    return {
      iso,
      d,
      total: evs.length,
      byConsultor,
    };
  });
  const maxCount = Math.max(1, ...dayStats.map((d) => d.total));
  const totalWeek = dayStats.reduce((s, d) => s + d.total, 0);
  // 10/08/2026 — Highlight the QUIETEST future day (lowest count among
  // days including today). Coordinator SAC can click any card to jump
  // to that day in the Week view; the quietest is visually emphasised
  // so it becomes the natural "book here" suggestion.
  const quietest = dayStats.reduce(
    (best, curr) => (curr.total < best.total ? curr : best),
    dayStats[0] || { total: Infinity, iso: null }
  );
  const openDay = (iso) => {
    if (!iso) return;
    // Compute the Monday of the week containing this day so the Week
    // view snaps to the right anchor.
    const [y, m, dd] = iso.split("-").map(Number);
    const d = new Date(y, m - 1, dd);
    d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const monday = ymd(d);
    navigate(`/rts-calendar?view=week&anchor=${monday}&highlight=${iso}`);
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5 lg:p-6 mb-6" data-testid="team-volume-widget">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0">
            <BarChart3 className="w-4 h-4 text-primary" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold">Coordenação SAC</div>
            <h3 className="font-ui text-lg text-foreground mt-0.5">Volume da equipa · próximos 7 dias</h3>
            <div className="text-[11.5px] text-muted-foreground mt-1">
              {loading ? "A carregar…" : (
                <><b data-testid="volume-total">{totalWeek}</b> RT(s) agendada(s) esta semana</>
              )}
            </div>
          </div>
        </div>
        <Link
          to="/rts-calendar?view=volume"
          data-testid="volume-link"
          className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted/70 border border-border text-foreground rounded-full px-3 py-1.5 font-semibold shrink-0"
        >
          Ver detalhe <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-6 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> A carregar…
        </div>
      ) : totalWeek === 0 ? (
        <div className="text-sm text-muted-foreground py-4 text-center">
          Sem RTs agendadas nos próximos 7 dias.
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-2">
          {dayStats.map((s, idx) => {
            const isToday = idx === 0;
            const isQuietest = s.iso === quietest.iso && quietest.total < maxCount;
            const pct = maxCount > 0 ? (s.total / maxCount) * 100 : 0;
            return (
              <button
                key={s.iso}
                type="button"
                onClick={() => openDay(s.iso)}
                className={`text-left rounded-xl border p-2 min-h-[110px] flex flex-col transition-all hover:border-primary/60 hover:shadow-sm cursor-pointer ${
                  isToday
                    ? "border-primary/40 bg-primary/5"
                    : isQuietest
                      ? "border-[hsl(var(--success))]/50 bg-[hsl(var(--success-bg))]"
                      : "border-border bg-muted/20"
                }`}
                data-testid={`volume-day-${s.iso}`}
                title={
                  isQuietest
                    ? `Menor carga da semana — click para agendar nesta data`
                    : `Abrir semana de ${s.iso}`
                }
              >
                <div className={`text-[10px] uppercase tracking-[0.15em] font-semibold text-center ${
                  isQuietest ? "text-[hsl(var(--success))]" : "text-muted-foreground"
                }`}>
                  {WEEKDAYS_PT[s.d.getDay()]}
                </div>
                <div className={`text-center text-lg font-semibold ${isToday ? "text-primary" : "text-foreground"}`}>
                  {s.d.getDate()}
                </div>
                {/* Bar */}
                <div className="mt-2 h-1.5 bg-muted/60 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${isQuietest ? "bg-[hsl(var(--success))]" : "bg-primary"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                {/* Count */}
                <div className="mt-2 text-center text-[13px] font-mono font-semibold text-foreground">
                  {s.total || "—"}
                </div>
                {/* Consultor dots */}
                <div className="mt-auto flex flex-wrap gap-1 justify-center pt-2">
                  {Object.entries(s.byConsultor).slice(0, 4).map(([cid, c]) => (
                    <span
                      key={cid}
                      title={`${c.name} · ${c.count} RT(s)`}
                      className="inline-block w-2 h-2 rounded-full"
                      style={{ backgroundColor: c.color }}
                    />
                  ))}
                  {Object.keys(s.byConsultor).length > 4 && (
                    <span className="text-[9px] text-muted-foreground">+{Object.keys(s.byConsultor).length - 4}</span>
                  )}
                </div>
                {isQuietest && (
                  <div className="mt-1 text-[9px] uppercase tracking-[0.1em] text-[hsl(var(--success))] font-semibold text-center">
                    ideal
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
